import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import type { InvestmentProperty } from "@aus-property/core";
import { Sparkles, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PropertyWizard } from "@/features/properties/PropertyWizard";

interface OnboardingFlowProps {
  onLoadDemo: () => Promise<void>;
  onCompleteWithoutDemo: () => Promise<void>;
  onCreateProperty: (property: InvestmentProperty) => Promise<void>;
}

const slides = [
  {
    title: "Made for Australian property investors",
    body: "AUD-focused portfolio tracking with practical assumptions, built in Brisbane.",
    bullets: [
      "AUD only (no currency confusion)",
      "Offset-aware loans (IO and P&I)",
      "Simple negative gearing and depreciation inputs",
      "Australian states and property types",
      "Australian-made product by a Brisbane team"
    ]
  },
  {
    title: "See what matters, fast",
    body: "Know where you stand across the whole portfolio.",
    bullets: ["Cashflow", "Equity", "LVR", "Yield and return metrics"]
  },
  {
    title: "Stress test scenarios before you act",
    body: "Model changes and compare outcomes side-by-side.",
    bullets: [
      "Interest rate shocks (+1% / +2%)",
      "Vacancy changes",
      "Extra repayments",
      "Sell in year X"
    ]
  },
  {
    title: "Private by default, backup when you want",
    body: "Everything runs on-device. Sign in only if you want backup and restore on another device.",
    bullets: [
      "Offline-first forecasting",
      "Optional account backup",
      "JSON/CSV export anytime",
      "No bank passwords requested"
    ]
  }
] as const;

export const OnboardingFlow = ({
  onLoadDemo,
  onCompleteWithoutDemo,
  onCreateProperty
}: OnboardingFlowProps) => {
  const [step, setStep] = useState(0);
  const [createMode, setCreateMode] = useState(false);
  const currentSlide = slides[step] ?? slides[0];

  const isFinalSlide = useMemo(() => step === slides.length - 1, [step]);

  if (createMode) {
    return (
      <div className="mx-auto max-w-4xl p-4 pb-28">
        <PropertyWizard
          onSave={async (property) => {
            await onCreateProperty(property);
            await onCompleteWithoutDemo();
          }}
          onCancel={() => setCreateMode(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center px-4 pb-24 pt-8">
      <div className="w-full max-w-xl space-y-4">
        <div className="flex items-center justify-center gap-2 text-app-primary">
          <Sparkles size={18} />
          <span className="text-sm font-semibold uppercase tracking-wider">
            Investment Property Mate
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.24 }}
          >
            <Card className="space-y-3 p-6">
              <h1 className="text-3xl font-semibold">{currentSlide.title}</h1>
              <p className="text-base text-app-soft">{currentSlide.body}</p>
              {"bullets" in currentSlide ? (
                <ul className="list-disc space-y-1 pl-5 text-sm text-app-soft">
                  {currentSlide.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}

              <div className="flex items-center gap-2 pt-2">
                {slides.map((_, index) => (
                  <span
                    key={index}
                    className={`h-1.5 rounded-full transition-all ${
                      index === step
                        ? "w-8 bg-app-primary"
                        : "w-3 bg-app-surface-muted"
                    }`}
                  />
                ))}
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => setStep((current) => Math.max(current - 1, 0))}
            disabled={step === 0}
          >
            <ChevronLeft size={16} /> Back
          </Button>

          {!isFinalSlide ? (
            <Button
              onClick={() =>
                setStep((current) => Math.min(current + 1, slides.length - 1))
              }
            >
              Next <ChevronRight size={16} />
            </Button>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onLoadDemo}>
                Explore AU demo
              </Button>
              <Button onClick={() => setCreateMode(true)}>
                Add your first property
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-app-soft">
          AUD only. Forecasts are informational and not financial advice.
        </p>
      </div>
    </div>
  );
};
